<template>
  <CelebrationOverlay :visible="visible" :max-width="588" @close="handleClose">
    <template #default="{ close }">
      <div class="pok-content">
        <div class="pok-inner">
          <div class="pok-header">
            <h2 id="pokCertificateDialogTitle" class="pok-title">Congratulations!</h2>
            <button class="pok-x-btn" aria-label="Close" @click="close">&times;</button>
          </div>
          <p id="pokCertificateDialogBody" class="pok-body">
            Congratulations on earning the
            <strong>{{ dialogData && dialogData.title }}</strong>
            certificate. Click to view or download your certificate. It will remain available in the Certificates
            section of your dashboard for future access.
          </p>
        </div>
        <button
          v-if="dialogData && dialogData.credential_url"
          class="pok-view-btn"
          @click="openCredential(dialogData.credential_url)"
        >
          View certificate
        </button>
      </div>
    </template>
  </CelebrationOverlay>
</template>

<script>
import { defineComponent } from 'vue';
import CelebrationOverlay from '@/components/ui/CelebrationOverlay.vue';

export default defineComponent({
  name: 'PokCertificateDialog',

  components: { CelebrationOverlay },

  props: {
    /** Controls visibility. Set to true to show the dialog + fire confetti. */
    visible: {
      type: Boolean,
      default: false
    },
    /** Credential data to display: { connection_id, title, credential_url } */
    dialogData: {
      type: Object,
      default: null
    }
  },

  emits: ['close'],

  methods: {
    handleClose() {
      this.$emit('close', this.dialogData?.connection_id);
    },

    /**
     * Opens the credential URL in a new tab.
     * P2: validates the scheme to prevent javascript:/non-https XSS vectors.
     */
    openCredential(url) {
      try {
        const parsed = new URL(url, window.location.origin);
        if (!['http:', 'https:'].includes(parsed.protocol)) return;
        window.open(parsed.toString(), '_blank', 'noopener,noreferrer');
      } catch {
        // ignore malformed URLs
      }
    }
  }
});
</script>

<style scoped>
/* POK congratulations content — Figma node 162:10356 */
.pok-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.pok-inner {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.pok-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.pok-title {
  flex: 1 0 0;
  margin: 0;
  font-family: Arial, sans-serif;
  font-size: 24px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: -0.03em;
  color: #191919;
  text-decoration: underline solid;
  text-underline-offset: -100%;
  text-decoration-thickness: 120%;
}

.pok-body {
  margin: 0;
  font-family: Arial, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #191919;
}
.pok-body strong {
  font-weight: 700;
}

/* Gold CTA — Figma: #ffc627, 40px tall, 32px h-padding, pill, min-width 112px */
.pok-view-btn {
  appearance: none;
  border: none;
  cursor: pointer;
  align-self: flex-start;
  min-width: 112px;
  height: 40px;
  padding: 0 32px;
  background: #ffc627;
  color: #191919;
  font-family: Arial, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  border-radius: 100px;
  transition:
    transform 0.12s ease,
    background 0.2s ease;
}
.pok-view-btn:hover {
  background: #ffd45e;
  transform: translateY(-2px);
}
.pok-view-btn:active {
  transform: translateY(0);
}

/* Plain xmark, no chip — Figma node I162:10356;74:4359 */
.pok-x-btn {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #191919;
  font-size: 26px;
  line-height: 1;
  padding: 0;
  transition: color 0.2s ease;
}
.pok-x-btn:hover {
  color: #8c1d40;
}
</style>
